import React from 'react';
import {  FiCheckCircle, FiClock,  FiZap, FiBarChart2, FiHeart, FiBookOpen, FiMessageSquare } from 'react-icons/fi';

const LMSEmpowerTeachers = () => {
  const empoweringFeatures = [
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Automate Repetitive Tasks',
      description: 'Teachers spend hours on administrative work—taking attendance, grading assignments, calculating marks, and generating reports. An LMS automates these repetitive tasks, freeing teachers to focus on what they do best: teaching and inspiring students.',
      stat: 'Teachers spend 8+ hours weekly on admin work',
      solution: 'Truyon automates attendance, grading, and reporting—saving teachers 80% of admin time.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Reusable Content Library',
      description: 'Teachers create lesson plans, presentations, and assignments once. With an LMS, they can reuse and update content year after year. No more starting from scratch every semester.',
      stat: '70% of teachers recreate content every year',
      solution: 'Truyon\'s content library lets teachers save, organize, and reuse materials effortlessly.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Real-Time Student Insights',
      description: 'Instead of guessing which students are struggling, teachers can see real-time data—attendance patterns, assignment completion, test scores, and engagement. This helps them provide targeted support.',
      stat: '90% of teachers say real-time insights improve teaching',
      solution: 'Truyon provides dashboards with complete student performance data.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: 'Streamlined Communication',
      description: 'Teachers spend hours answering parent emails, sending reminders, and sharing updates. An LMS centralizes communication—announcements, messages, and notifications in one place.',
      stat: 'Teachers spend 2-3 hours weekly on communication',
      solution: 'Truyon\'s communication tools keep parents informed automatically.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Personalized Learning Paths',
      description: 'Every student learns differently. With an LMS, teachers can create personalized learning paths—assign different content to different students based on their needs and progress.',
      stat: '85% of teachers want to personalize learning',
      solution: 'Truyon enables differentiated instruction with personalized assignments.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiHeart className="w-6 h-6" />,
      title: 'Professional Development',
      description: 'An LMS can also support teacher development—sharing best practices, offering training modules, and building a collaborative teaching community. Empowered teachers are supported teachers.',
      stat: '70% of teachers value professional development opportunities',
      solution: 'Truyon includes professional development features for continuous teacher growth.',
      color: 'bg-blue-50'
    }
  ];

  const pitfalls = [
    {
      title: 'Overloading Teachers with Too Many Features',
      description: 'When teachers are introduced to complex LMS platforms with hundreds of features, they feel overwhelmed. Simplicity is key to adoption.'
    },
    {
      title: 'No Training or Support',
      description: 'Implementing an LMS without proper training leads to frustration and abandonment. Teachers need ongoing support.'
    },
    {
      title: 'Increasing Administrative Work',
      description: 'Some LMS platforms add to the administrative burden instead of reducing it. Teachers feel they are doing more work, not less.'
    },
    {
      title: 'Expecting Immediate Adoption',
      description: 'Change takes time. Teachers need patience, encouragement, and time to adapt to the new system.'
    },
    {
      title: 'Lack of Integration with Existing Systems',
      description: 'When teachers have to use multiple platforms—one for attendance, one for grading, one for LMS—they get exhausted. Integration is essential.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Teachers are the heart of education. They inspire students, create engaging lessons, and shape futures. But in recent years, teachers have become overwhelmed—not by teaching, but by administrative work, grading, communication, and the constant pressure to do more with less.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        A Learning Management System should be a tool that empowers teachers, not exhausts them. <strong className="text-slate-900">Unfortunately, many LMS platforms do the opposite.</strong> They add complexity, increase workload, and frustrate teachers who are already stretched thin.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how an LMS can actually empower teachers and professors—without burning them out.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">8+</div>
          <div className="text-sm text-slate-500">Hours weekly on admin work</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Teachers want real-time insights</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">70%</div>
          <div className="text-sm text-slate-500">Teachers value professional development</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How an LMS Empowers Teachers</h2>

      {/* Empowering Features */}
      {empoweringFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Pitfalls Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What NOT to Do: LMS Pitfalls That Exhaust Teachers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pitfalls.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">✖️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">LMS That Exhausts vs. LMS That Empowers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">LMS That Exhausts Teachers</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Complex interface with too many features</span></li>
              <li className="flex items-start gap-2">• <span>No training or support</span></li>
              <li className="flex items-start gap-2">• <span>Adds to administrative workload</span></li>
              <li className="flex items-start gap-2">• <span>Doesn't integrate with existing systems</span></li>
              <li className="flex items-start gap-2">• <span>No time savings, only new tasks</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">LMS That Empowers Teachers (Truyon)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Simple, intuitive interface</span></li>
              <li className="flex items-start gap-2">• <span>Minimal training, ongoing support</span></li>
              <li className="flex items-start gap-2">• <span>Reduces administrative workload</span></li>
              <li className="flex items-start gap-2">• <span>Integrated ERP + LMS platform</span></li>
              <li className="flex items-start gap-2">• <span>Saves 80% of admin time</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Teacher's Perspective */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">What Teachers Actually Want from an LMS</h3>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span><strong>Simplicity:</strong> "I want to spend 5 minutes learning the system, not 5 hours."</span></li>
          <li className="flex items-start gap-2">• <span><strong>Time Savings:</strong> "I want the LMS to save me time, not add to my workload."</span></li>
          <li className="flex items-start gap-2">• <span><strong>Support:</strong> "I want help when I need it, not just at the beginning."</span></li>
          <li className="flex items-start gap-2">• <span><strong>Integration:</strong> "I want one platform for everything—not separate systems."</span></li>
          <li className="flex items-start gap-2">• <span><strong>Student Insights:</strong> "I want to know which students need help, quickly and clearly."</span></li>
        </ul>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Empowers Teachers, Not Exhausts Them</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">😊</div>
            <h4 className="font-bold text-slate-900 text-sm">Simple & Intuitive</h4>
            <p className="text-xs text-slate-600">Teachers start using it in minutes</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Automates Admin Work</h4>
            <p className="text-xs text-slate-600">Attendance, grading, and reports automated</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Insights</h4>
            <p className="text-xs text-slate-600">See student progress instantly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">Integrated Platform</h4>
            <p className="text-xs text-slate-600">ERP + LMS in one place</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Reusable Content</h4>
            <p className="text-xs text-slate-600">Save and reuse lessons and materials</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Ongoing Support</h4>
            <p className="text-xs text-slate-600">Help available when needed</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Empower Your Educators with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Teachers don't need another tool that adds to their workload. They need a tool that makes their life easier—automating repetitive tasks, providing real-time insights, and saving precious time.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is designed to empower teachers. Simple interface, automated admin work, real-time student insights, and seamless integration with your existing systems. Teachers spend less time on paperwork and more time on what matters most—teaching.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for LMS.</strong> Empowering teachers. Transforming education.
        </p>
      </div>
    </>
  );
};

export default LMSEmpowerTeachers;